import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import { stringify } from 'qs';
import { v4 as uuidv4 } from 'uuid';
import { useNetwork } from 'choco-miniapp-debugbar';
import i18n from '@/utils/plugins/i18n';
import { country } from '@/utils/plugins/config';
import {
	ApiProxyResponse,
	isApiProxyResponse
} from './types/interfaces/api-proxy-response-data';
import { isApiProxyErrorData } from './types/interfaces/api-proxy-error-data';
import { AUTH_REQUEST, GET_TRACK_ID_REQUEST } from './constants';
import { events } from '@/services/analytics/enums';
import {
	AUTH_SESSION_ENDED_ERROR,
	AUTH_STALE_TOKEN_ERROR,
	AUTH_TIMEOUT_ERROR,
	NATIVE_APP_AGENT
} from '@/utils/constants';

type SentryApiType = 'error' | 'warning';

interface AuthProvider {
	isAuthorized: () => boolean;
	getAccessToken: () => string;
	setAccessToken: (token: string) => void;
	goToOauth: () => void;
	logout: () => Promise<void> | void;
}

interface SentryProvider {
	captureApiException: (error: any, type: SentryApiType) => void;
}

interface AnalyticsProvider {
	logEvent: (
		event: string,
		payload?: Record<string, any>,
		options?: { flush?: boolean }
	) => Promise<void> | void;
}

interface providers {
	application: {
		clearUserId: () => void;
	};
	auth: {
		authorize: () => Promise<void | boolean>;
	};
}

const TRACKED_APIS: [string, SentryApiType][] = [
	[AUTH_REQUEST, 'warning'],
	[GET_TRACK_ID_REQUEST, 'error']
];

const baseURL =
	import.meta.env.VITE_API_PROXY ||
	(import.meta.env.VITE_ENV === 'production'
		? 'https://api-proxy.choco.kz'
		: 'https://api-proxy.chocodev.kz');
const { addRequest, addRequestResponseById } = useNetwork();

const client = axios.create({
	baseURL,
	paramsSerializer: params => stringify(params),
	transformRequest: [
		data => {
			try {
				// Prevent double-stringifying
				return typeof data === 'string' ? data : JSON.stringify(data);
			} catch (e) {
				// Fallback if data can't be stringified
				return data;
			}
		}
	]
});

axiosRetry(client, {
	retries: 3,
	retryDelay: retryCount => {
		return 1000 * retryCount;
	}
});

// Нативный коллбэк может не прийти вовсе: choco-app бросает исключение прямо
// из onAuthError, мимо промиса, и тот не сеттлится никогда. Без потолка запрос
// висит вечно, а кнопка остаётся с лоадером.
const AUTHORIZE_TIMEOUT = 30000;

const setupInterceptors = (
	authProvider: AuthProvider,
	sentryProvider: SentryProvider,
	providers: providers,
	analyticsProvider: AnalyticsProvider
) => {
	// Бридж супераппа держит один глобальный слот коллбэка
	// (window.RahmetWebApp.onAuthSuccess / onAuthSuccessV2). Параллельные
	// authorize() перетирают его, и все промисы, кроме последнего, не
	// резолвятся никогда. Поэтому на весь залп 401 — одна попытка авторизации.
	let authorizing: Promise<void> | null = null;
	let loggingOut = false;

	const authorizeOnce = (): Promise<void> => {
		if (authorizing) {
			return authorizing;
		}

		// Снимок делаем один раз на попытку, а не на каждый запрос залпа:
		// иначе второй и последующие запросы сравнивали бы уже обновлённый
		// токен сам с собой и считали авторизацию неудачной.
		const tokenBefore = authProvider.getAccessToken();

		authorizing = new Promise<void>((resolve, reject) => {
			const timer = setTimeout(() => {
				reject(new Error(AUTH_TIMEOUT_ERROR));
			}, AUTHORIZE_TIMEOUT);

			Promise.resolve(providers.auth.authorize())
				.then(() => {
					// authorize() умеет завершаться «успешно», не обновив токен:
					// authByTrackId возвращает false, когда trackId не изменился.
					// Ретрай с тем же мёртвым токеном — гарантированный 401.
					if (authProvider.getAccessToken() === tokenBefore) {
						reject(new Error(AUTH_STALE_TOKEN_ERROR));
						return;
					}

					resolve();
				})
				.catch(reject)
				.finally(() => clearTimeout(timer));
		}).finally(() => {
			authorizing = null;
		});

		return authorizing;
	};

	const captureApiException = (error: any) => {
		for (const [req, type] of TRACKED_APIS) {
			if (req.includes(error.response?.config?.url || '')) {
				sentryProvider.captureApiException(error, type);
				break;
			}
		}
	};

	const logout = async (
		response: AxiosResponse | AxiosError,
		reason: 'retry_unauthorized' | 'authorize_failed'
	): Promise<void> => {
		// Залп 401 приводит сюда каждый запрос по отдельности: без флага
		// событие разлогина улетело бы в аналитику столько раз, сколько было
		// параллельных запросов.
		if (loggingOut) {
			return;
		}

		loggingOut = true;

		// Дожидаемся доставки события (beacon + flush) до reload/redirect,
		// иначе оно теряется при уходе со страницы и не доходит до Amplitude.
		await analyticsProvider.logEvent(
			events.REFRESH_ERROR_AND_LOGOUT,
			{
				reason,
				url: response.config?.url,
				method: response.config?.method
			},
			{ flush: true }
		);
		await authProvider.logout();
		window.location.reload();
	};

	// На RN (ChocoPartnerApp) authorize() синхронно уводит вебвью на OAuth
	// через goToOauth (window.location.href), а его промис никогда не
	// резолвится/реджектится — поэтому обычный logout() с ивентом на этом
	// пути недостижим и разлогин не долетает до Amplitude. Логируем ивент
	// здесь, до редиректа, и только потом отдаём управление authorize().
	const logoutOnPartnerApp = async (
		response: AxiosResponse | AxiosError
	): Promise<void> => {
		await analyticsProvider.logEvent(
			events.REFRESH_ERROR_AND_LOGOUT,
			{
				reason: 'redirect_to_oauth',
				url: response.config?.url,
				method: response.config?.method
			},
			{ flush: true }
		);
		providers.auth.authorize();
	};

	// Каждая ветка обязана сеттлить промис: незавершённый запрос оставляет
	// Promise.all вызывающего кода висеть, а вместе с ним — лоадеры и флаги,
	// которые снимаются только в finally.
	const handleUnauthorizedResponse = (
		client: AxiosInstance,
		response: AxiosResponse | AxiosError
	): Promise<AxiosResponse<any, any>> => {
		const appEnv = import.meta.env.VITE_ENV;

		if (appEnv === 'local') {
			const newToken = prompt('Новый токен');

			if (newToken) {
				authProvider.setAccessToken(newToken);
				return client({ ...response.config });
			}
		}

		if (navigator.userAgent.includes(NATIVE_APP_AGENT)) {
			logoutOnPartnerApp(response);
			return Promise.reject(new Error(AUTH_SESSION_ENDED_ERROR));
		}

		// Ретрай со свежим токеном снова получил 401 — значит сессия мертва
		if (
			Object.prototype.hasOwnProperty.call(response.config, '_retryAttempts')
		) {
			logout(response, 'retry_unauthorized');
			return Promise.reject(new Error(AUTH_SESSION_ENDED_ERROR));
		}

		const config = { ...response.config, _retryAttempts: 1 };

		return authorizeOnce().then(
			() => client(config),
			(error: unknown) => {
				const message = error instanceof Error ? error.message : '';

				// Токен не обновился — переавторизоваться нечем, сессия мертва
				if (message === AUTH_STALE_TOKEN_ERROR) {
					logout(response, 'authorize_failed');
					return Promise.reject(new Error(AUTH_SESSION_ENDED_ERROR));
				}

				// Отмена нативного шита, ошибка бриджа или таймаут. Разлогинивать
				// нельзя: пользователь просто отказался переавторизоваться сейчас,
				// а разлогин здесь и приводил к «разлогин без возможности входа».
				return Promise.reject(
					error instanceof Error ? error : new Error(AUTH_SESSION_ENDED_ERROR)
				);
			}
		);
	};

	client.interceptors.request.use(
		config => {
			const fingerprint = uuidv4();
			config.headers['X-Fingerprint'] = fingerprint;
			config.headers['X-Language'] = i18n.global.locale.value;

			if (!config.headers['X-Country'] && country.value?.code) {
				config.headers['X-Country'] = country.value.code;
			}

			if (!config.headers['Content-Type']) {
				config.headers['Content-Type'] = 'application/json';
			}

			if (!config.headers['X-Idempotency-key']) {
				config.headers['X-Idempotency-key'] = uuidv4();
			}

			if (authProvider.isAuthorized()) {
				// eslint-disable-next-line no-param-reassign
				config.headers.Authorization = `Bearer ${authProvider.getAccessToken()}`;
			}

			addRequest({
				id: fingerprint,
				headers: config.headers,
				params: config.params,
				body: config.data,
				url: config.url ? `${baseURL}/${config.url}` : baseURL,
				method: config.method || 'GET'
			});

			return config;
		},
		error => Promise.reject(error)
	);

	client.interceptors.response.use(
		response => {
			const fingerprint = response.config.headers['X-Fingerprint'];
			if (fingerprint) {
				addRequestResponseById(fingerprint, response.status, response.data);
			}

			const data: ApiProxyResponse<any> | any = response.data;

			// gateway
			if (isApiProxyResponse<unknown>(data)) {
				if (data.error_code && data.error_code !== 200) {
					captureApiException(response);

					if (data.error_code === 401) {
						return handleUnauthorizedResponse(client, response);
					}

					return Promise.reject(data);
				}
			}

			return response;
		},
		(error: Error | AxiosError) => {
			if (axios.isAxiosError(error)) {
				const fingerprint = error.config?.headers['X-Fingerprint'];
				if (fingerprint) {
					addRequestResponseById(
						fingerprint,
						error.status || 404,
						error.response?.data
					);
				}

				// назначаю message первой ошибки в ApiProxy
				const data = error.response?.data;
				if (isApiProxyErrorData(data) && data.errors?.length) {
					error.message = data.errors[0].title;
				} else if (isApiProxyResponse<unknown>(data)) {
					error.message = data.message;
				}

				// обработка 401
				const statusCode = error.response?.status || 0;
				if (statusCode === 401) {
					return handleUnauthorizedResponse(client, error);
				}

				captureApiException(error);
			}

			return Promise.reject(error);
		}
	);
};

export { client, setupInterceptors };
